import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { toast } from "react-toastify";
import { useLazyGetOrdersQuery } from "./slices/OrdersSlice";
import { belongsToOutlet, mergeOrders } from "./slices/ordersCacheSlice";
import { getCurrentDate } from "./utils/getCurrentDate";

const ONLINE_ORDERS_POLL_MS = 5 * 60 * 1000;

// The home page lists only successfully placed (payment done) orders, i.e.
// orders in one of these statuses. Anything else - Payment_Pending,
// Not_Placed, or a missing / unknown status - is left out of the list and
// the metrics. Filtered here instead of in the api query so that an order
// moving out of these statuses still reaches the cache and stops being
// listed with its old status
const LISTED_STATUSES = [
  "Placed",
  "Confirmed",
  "Preparing",
  "Ready",
  "In_Delivery",
  "Delivered",
  "Delivery_Rejected",
  "Could_Not_Deliver",
  "Rejected",
];

const isListed = (order) => LISTED_STATUSES.includes(order?.orderStatus);

const EMPTY_ORDERS = [];

// Start of today in IST, same boundary the home page has always used
const getDayStart = () =>
  new Date(
    new Date(getCurrentDate()).getTime() - 5.5 * 60 * 60 * 1000
  ).toISOString();

// Keeps today's orders of the selected outlet in redux (state.ordersCache).
// - On load (and on every socket new-order signal) only orders updated since
//   the last retrieval are fetched and merged into the cached list.
// - Every 5 minutes online orders updated since the last retrieval are
//   fetched as a backup, in case a new-order socket message never arrived.
const useOrdersCache = () => {
  const store = useStore();
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const { newOrders } = useSelector((state) => state.newOrders);
  const ordersCache = useSelector((state) => state.ordersCache);
  const selectedOutletId = userInfo?.selectedOutletId ?? null;

  const [getOrders] = useLazyGetOrdersQuery();
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);

  // Reads the store at call time, so the 5 minute timer never works with a
  // stale snapshot of the cache
  const syncOrders = useCallback(
    async ({ onlineOnly = false } = {}) => {
      const { auth, ordersCache: cache } = store.getState();
      const token = auth.userInfo?.token;
      const outletId = auth.userInfo?.selectedOutletId ?? null;
      if (!token) return;

      const day = getCurrentDate();
      const dayStart = getDayStart();
      const cacheUsable =
        cache.outletId === outletId &&
        cache.day === day &&
        Boolean(cache.lastRetrievedTime);
      // Without a usable cache (first load, new day, outlet switched) the
      // backup poll falls back to a full fetch of today's orders
      const online = onlineOnly && cacheUsable;
      const cursor = online
        ? cache.lastOnlineRetrievedTime
        : cache.lastRetrievedTime;

      let query = `createdAt[gte]=${dayStart}&sort=-createdAt`;
      if (cacheUsable) query += `&updatedAt[gte]=${cursor}`;
      if (online) query += `&orderType=online`;

      if (!cacheUsable) setFetching(true);
      try {
        let fetched = [];
        try {
          const res = await getOrders({ query, token }).unwrap();
          fetched = Array.isArray(res) ? res : [];
          setError(null);
        } catch (err) {
          // The api answers "no orders found" with a 4xx status - that is an
          // empty result. Network / server failures leave the cache untouched
          if (typeof err?.status !== "number" || err.status >= 500) {
            if (!cacheUsable) setError(err);
            return;
          }
          if (!cacheUsable) setError(err);
        }

        // A response for an outlet that is no longer selected is discarded
        if ((store.getState().auth.userInfo?.selectedOutletId ?? null) !== outletId)
          return;

        if (online) {
          const known = new Set(
            store.getState().ordersCache.orders.map((order) => order._id)
          );
          const missed = fetched.filter(
            (order) =>
              belongsToOutlet(order, outletId) &&
              !known.has(order._id) &&
              isListed(order)
          );
          if (missed.length > 0) {
            toast.info(
              `${missed.length} new online order${
                missed.length > 1 ? "s" : ""
              } received`
            );
          }
        }

        dispatch(
          mergeOrders({
            outletId,
            day,
            dayStart,
            orders: fetched,
            reset: !cacheUsable,
            onlineOnly: online,
          })
        );
      } finally {
        setFetching(false);
      }
    },
    [store, dispatch, getOrders]
  );

  // On page load, when the outlet changes and when a socket message
  // announces a new order
  useEffect(() => {
    syncOrders();
  }, [newOrders, selectedOutletId, syncOrders]);

  // Backup for missed socket messages
  useEffect(() => {
    const timer = setInterval(
      () => syncOrders({ onlineOnly: true }),
      ONLINE_ORDERS_POLL_MS
    );
    return () => clearInterval(timer);
  }, [syncOrders]);

  const cacheMatches =
    ordersCache.outletId === selectedOutletId &&
    ordersCache.day === getCurrentDate();

  const orders = useMemo(
    () =>
      cacheMatches ? ordersCache.orders.filter(isListed) : EMPTY_ORDERS,
    [cacheMatches, ordersCache.orders]
  );

  return {
    orders,
    isLoading: fetching && orders.length === 0,
    error: orders.length === 0 ? error : null,
    refreshOrders: syncOrders,
  };
};

export default useOrdersCache;
